import {ConfirmationDialog, FormDialog, fullDate} from '@management-ui/core';
import {Add, Delete, Edit, OpenInBrowser} from '@mui/icons-material';
import {Alert, IconButton, Snackbar, Tooltip} from '@mui/material';
import React, {useCallback, useContext, useState} from 'react';
import Currency from '../../../../components/Currency';
import {ServiceContext} from '../../../../components/Services';
import Table from '../../../../components/Table';
import {trialSummary, vehicleTitle} from '../../../../formatters';
import SubscriptionItemForm from '../../forms/SubscriptionItemForm';

const Items = ({subscription, onSubscriptionUpdated, canEdit, canActivate}) => {
  const services = useContext(ServiceContext);
  const [showActivate, setShowActivate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activationLength, setActivationLength] = useState('');
  const [showActivated, setShowActivated] = useState(false);
  const [showActivationFailed, setShowActivationFailed] = useState(false);

  const handleActivate = useCallback((confirmed) => {
    setShowActivate(false);
    if (confirmed) {
      setLoading(true);
      services.subscription.activateSubscriptionItem(subscription, selected)
        .then((length) => {
          setLoading(false);
          setActivationLength(length);
          setShowActivated(true)
        })
        .catch(() => {
          setLoading(false);
          setShowActivationFailed(true);
        });
    }
  }, [services, subscription, selected]);

  const handleDelete = useCallback((confirmed) => {
    setShowDelete(false);
    if (confirmed) {
      setLoading(true);
      services.subscription.deleteSubscriptionItem(subscription, selected).then(updated => {
        setLoading(false);
        onSubscriptionUpdated(updated);
      }).catch(() => setLoading(false));
    }
  }, [services, subscription, selected, onSubscriptionUpdated]);

  return (
    <>
      <Table className={loading ? 'loading' : ''}>
        <Table.Actions>
          <h3>Items</h3>
          <Tooltip title="Add New Item">
            <span>
              <IconButton
                onClick={() => setSelected(null) || setShowEdit(true)}
                disabled={!canEdit}>
                <Add/>
              </IconButton>
            </span>
          </Tooltip>
        </Table.Actions>
        <Table.Row>
          <Table.Header>Product</Table.Header>
          <Table.Header>Vehicle</Table.Header>
          <Table.Header>Price</Table.Header>
          <Table.Header>Discount</Table.Header>
          <Table.Header>Total</Table.Header>
          <Table.Header>Trial</Table.Header>
          <Table.Header>Expires</Table.Header>
          <Table.Action/>
          <Table.Action/>
          <Table.Action/>
        </Table.Row>
        {subscription.items.length > 0 ? (
          subscription.items.map((item, index) => (
            <Table.Row key={index}>
              <Table.Column>{item.product ? <>{item.product.name}<br/>({item.product.length > 1 ? `${item.product.length} months` : 'monthly'})</> : '-'}</Table.Column>
              <Table.Column>{item.vehicle ? vehicleTitle(item.vehicle) : '-'}</Table.Column>
              <Table.Column><Currency amount={item.price}/></Table.Column>
              <Table.Column><Currency amount={item.discount}/></Table.Column>
              <Table.Column><Currency amount={item.total}/></Table.Column>
              <Table.Column>{trialSummary(item)}</Table.Column>
              <Table.Column>{item.expires_at ? fullDate(item.expires_at) : 'N/A'}</Table.Column>
              <Table.Action>
                <Tooltip title="Activate Item">
                  <span>
                    <IconButton
                      onClick={() => setSelected(item) || setShowActivate(true)}
                      disabled={!canActivate}>
                      <OpenInBrowser/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
              <Table.Action>
                <Tooltip title="Edit Item">
                  <span>
                    <IconButton
                      onClick={() => setSelected(item) || setShowEdit(true)}
                      disabled={!canEdit}>
                      <Edit/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
              <Table.Action>
                <Tooltip title="Delete Item">
                  <span>
                    <IconButton
                      onClick={() => setSelected(item) || setShowDelete(true)}
                      disabled={!canEdit}>
                      <Delete/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
            </Table.Row>
          ))
        ) : (
          <Table.NoRecords>There are no items associated with this subscription</Table.NoRecords>
        )}
      </Table>
      <ConfirmationDialog
        title="Activate Item"
        message={[
          'Are you sure you want to activate this item with Scorpion?',
          subscription.is_recurring ?
            'Since this subscription is monthly, the item will be activated for 1 month and 2 days.' :
            'Since this subscription is fixed length, the item will be activate until its expiration date.'
        ].join(' ')}
        open={showActivate}
        onClose={handleActivate}
      />
      <FormDialog
        title={selected ? 'Edit Item' : 'Add New Item'}
        open={showEdit}
        maxWidth="md"
        onClose={() => setShowEdit(false)}
        render={(props) => (
          <SubscriptionItemForm
            {...props}
            subscription={subscription}
            item={selected ?? {}}
            onSaved={(updated) => setShowEdit(false) || onSubscriptionUpdated(updated)}
          />
        )}
      />
      <ConfirmationDialog
        title="Delete Item"
        message="Are you sure you want to delete this item?"
        open={showDelete}
        onClose={handleDelete}
      />
      <Snackbar open={showActivated} autoHideDuration={4000} onClose={() => setShowActivated(false)}>
        <Alert onClose={() => setShowActivated(false)} severity="success">
          The item was successfully activated for {activationLength}
        </Alert>
      </Snackbar>
      <Snackbar open={showActivationFailed} autoHideDuration={4000} onClose={() => setShowActivationFailed(false)}>
        <Alert onClose={() => setShowActivationFailed(false)} severity="error">
          The item could not be activated. This can happen if the associated vehicle has not been registered with
          scorpion or if the stock is not allocated correctly.
        </Alert>
      </Snackbar>
    </>
  );
};

export default Items;
